/**
 * Centralized banner store.
 * Shows non-blocking, auto-dismissing banners from anywhere in the app.
 * Replaces all Alert.alert() calls for errors, success, and info messages.
 */

import { create } from 'zustand';

export type BannerType = 'error' | 'warning' | 'info' | 'success';

interface BannerState {
  visible: boolean;
  message: string;
  type: BannerType;
  action?: { label: string; onPress: () => void };
  /**
   * Monotonically increasing per `showBanner` call. Lets the AppBanner
   * component's auto-dismiss effect distinguish "the same banner is
   * still up" from "showBanner was called again with the same
   * message/type" — without this, two consecutive consent-grant
   * failures (same message string, same type) wouldn't reset the
   * timer, and the dismiss from the FIRST call would fire mid-way
   * through the second banner's intended display window. Surfaced by
   * CONV-009 in the 14:20 run after the `dev.set-auth` swap tightened
   * branch-to-branch gaps from ~9 s to ~1 s.
   */
  showCount: number;
}

interface AppBannerStore {
  banner: BannerState;
  showBanner: (opts: {
    message: string;
    type?: BannerType;
    action?: { label: string; onPress: () => void };
  }) => void;
  dismiss: () => void;
}

const INITIAL_BANNER: BannerState = {
  visible: false,
  message: '',
  type: 'info',
  showCount: 0,
};

export const useAppBannerStore = create<AppBannerStore>((set) => ({
  banner: INITIAL_BANNER,

  showBanner: ({ message, type = 'error', action }) => {
    set((state) => ({
      banner: {
        visible: true,
        message,
        type,
        action,
        // Reset the timer by incrementing the counter even if the
        // banner is already visible with the identical message + type.
        // The AppBanner effect's deps include showCount so a new
        // showBanner call always clears the prior dismiss timeout
        // and starts a fresh AUTO_DISMISS_MS window.
        showCount: state.banner.showCount + 1,
      },
    }));
  },

  dismiss: () => {
    // Preserve showCount across the dismiss → showBanner cycle so a
    // future showBanner still increments monotonically. (Resetting to
    // 0 would be harmless today, but a future caller comparing two
    // showCount snapshots would be surprised by a count going down.)
    set((state) => ({
      banner: { ...INITIAL_BANNER, showCount: state.banner.showCount },
    }));
  },
}));
